import { APP_TENANT, APP_URL, getEnvironment } from "../constants/app-url"
import { replaceTenant } from "./url"
import { getUUIDConversion, setUUIDConversion } from "./uuid"

const parseStrUrl = (tempIframeURL) => {
  try {
    // If an iframeURL is saved as a stringified object, it is parsed here and we select the iframe URL based on the appropriate region.
    // Currently only eu and us mapping is allowed, based on browser URL
    return JSON.parse(tempIframeURL)
  } catch (e) {
    // If parsing fails, keep the original string value
    return null
  }
}

export const parseIframeURLs = (
  iframeURL,
  appTenantVal,
  storeData,
  useSSEEUUrl
) => {
  let tempIframeURL = iframeURL
  let unmodifiedTenantHost: string = APP_TENANT.substring(
    0,
    APP_TENANT.length - 1
  )
  const parsedUrl = parseStrUrl(tempIframeURL)

  if (typeof parsedUrl === "object" && parsedUrl !== null) {
    // Guard the FW/SSE hint lookup — a tenant with a region-mapped iframe
    // URL but missing hints crashed here (fwID.id on undefined), which
    // propagated to getAppConfig's catch and 401'd the whole login.
    // Mirrors the guarded pattern in logoutSingleApp.
    const userInfoTenants = storeData?.userInfo?.ext?.tenantId || []
    const sseID = userInfoTenants.find((a) => a?.hint === "SSE")
    const fwID = userInfoTenants.find((a) => a?.hint === "FW")
    let region = useSSEEUUrl ? "eu" : "us"
    if (fwID?.id && sseID?.id) {
      try {
        const data = setUUIDConversion(fwID.id, sseID.id)
        const conv = getUUIDConversion(data)
        region = conv.includes("203_") || useSSEEUUrl ? "eu" : "us"
      } catch (e) {
        console.error("[parseIframeURLs] Region detection failed:", e)
      }
    }
    // Fall back to the other region's URL if the selected one is absent —
    // parsedUrl[region] being undefined crashed the .replace() chain below.
    tempIframeURL =
      parsedUrl[region] ??
      parsedUrl[region === "eu" ? "us" : "eu"] ??
      iframeURL
  }

  return tempIframeURL
    .replace("{tenantHost}", unmodifiedTenantHost)
    .replace("OIDC_ACCESS_TOKEN", storeData.userToken)
    .replace("ENV", getEnvironment())
    .replace("TENANT", appTenantVal)
    .replace("USERNAME", storeData?.userInfo?.userName)
}

/**
 * Portal host without the tenant label, e.g. "forcepointone.com" or
 * "app.forcepoint.io". Lets external apps (DSPM) send the user back to the
 * OIDC host they actually logged into instead of a hardcoded domain.
 */
const getAppDomain = (): string => {
  const host = APP_URL?.split("//")[1]?.split("/")[0] ?? ""
  return host.startsWith(APP_TENANT) ? host.slice(APP_TENANT.length) : host
}

export const parseApplicationURL = (
  applicationURL: string,
  tenants: any[],
  unmodifiedTenantHost: string
) => {
  if (!applicationURL) return applicationURL

  return applicationURL
    .replace(/\{tenantHost\}/g, unmodifiedTenantHost)
    .replace(/\{appDomain\}/g, getAppDomain())
    .replace(/\{([^{}:]+):([^{}]*)\}/g, (_match, hint: string, defaultValue: string) => {
      const tenantId = tenants?.find(
        (a) => a?.hint?.toLowerCase() === hint.toLowerCase()
      )?.id
      return tenantId ?? defaultValue
    })
}

export const parseAppModules = (modules, appTenantVal, domain) => {
  if (!modules) {
    return []
  }
  for (const module of modules) {
    module.moduleUrls.prefix = replaceTenant(
      module.moduleUrls.prefix,
      appTenantVal,
      domain
    )
    module.moduleUrls.apiURL = replaceTenant(
      module.moduleUrls.apiURL,
      appTenantVal,
      domain
    )
  }
  return modules
}
